import { Ref } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { useMediaById } from "@coworker/app/src/components/RoomSettings/hooks/useMediaById";
import { useTranslation } from "@coworker/locales";
import TitledTextWithModal from "@coworker/apprestructured/src/task-management/components/TitledTextWithModal/TitledTextWithModal";
import { getMediaTypeText } from "@coworker/app/src/components/RoomSettings/tools";
import { MediaTypes } from "@coworker/app/src/components/RoomSettings/constants";
import { AreaHfb } from "@coworker/app/src/components/RoomSettings/types/media";
import { getDepartmentsOptions } from "@coworker/app/src/helpers/departmentsOptions";
import { getHfbForDisplay } from "./MediaLocationCreatorFragment.helper";

export type MediaLocationCreatorFragmentConfigProps = {
  field: string;
  title: string;
};

type MediaLocationCreatorFragmentProps = {
  configProps: MediaLocationCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function MediaLocationCreatorFragment({
  configProps,
  commonProps,
}: MediaLocationCreatorFragmentProps) {
  const { field, title } = configProps;
  const { task, taskType } = commonProps;
  const mediaId = validateInitialDetailsFieldValue<string>(
    getByPath(task, field),
    field,
    taskType
  );
  const { data: mediaResponse, isLoading: isMediaLoading } = useMediaById(
    isLoading(mediaId) ? null : mediaId
  );
  const { t } = useTranslation();

  const media = mediaResponse?.media;

  if (isLoading(mediaId) || isMediaLoading) {
    return <LoadingCreatorFragment />;
  }

  if (!media) {
    return null;
  }

  const mediaType = (media.mediaType ?? "NONE") as MediaTypes;
  const options = getDepartmentsOptions(t);
  const hfbForDisplay = getHfbForDisplay({
    hfb: (media as AreaHfb).areaHfb,
    options,
    t,
  });

  return (
    <TitledTextWithModal
      title={t(title)}
      text={media.name}
      modalHeader={t(title)}
      modalCaption={getMediaTypeText(mediaType)}
      modalFirstLine={media.name}
      modalSubsequentLines={hfbForDisplay ? [hfbForDisplay] : []}
    />
  );
}

export default MediaLocationCreatorFragment;
